/* eslint-disable max-len */
import React, { useContext, useEffect } from 'react';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { Row } from '@thd-olt-component-react/grid';
import {
  arrayOf as arrayType,
  extend,
  params,
  QueryContext,
  shape as shapeType,
  string as stringType,
  useDataModel,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { node } from 'prop-types';
import { RatingsAndReviewsDesktopImporter, RatingsAndReviewsMobileImporter } from '@thd-olt-component-react/ratings-and-reviews';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { ReviewsRedirector } from '@thd-nucleus/app-render';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar'
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs'
import { ProductDetails } from '@thd-olt-component-react/product-details'
import { SalientPoints } from '@thd-olt-component-react/salient-points'
import { MediaGalleryDesktop, MediaGalleryCarousel } from '@thd-olt-component-react/media-gallery';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { DynamicRecsWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { PprMetadata } from '@thd-olt-component-react/metadata';
import { Sharebelt, ShareBeltShareButton, ShareBeltPrint } from '@thd-olt-component-react/thd-sharebelt';
import ViewDetailsLink from '../components/ViewDetailsLink';
import ProductReviewsHeader from '../components/ProductReviewsHeader';
import ProductDetailsReviewsRatings from '../components/ProductDetailsReviewsRatings';
import DynamicRecsWrapperImporter from '../components/DynamicRecsWrapperImporter';

export const ProductReviews = () => {
  declareContexts([ExperienceContext, QueryContext]);
  const qctx = useContext(QueryContext);
  const { channel } = useContext(ExperienceContext);
  const { itemId, page } = useParams();
  const { pathname, search } = useLocation();
  let emtLinksPath;
  if (page) {
    emtLinksPath = pathname;
  } else if (pathname.endsWith('/')) {
    emtLinksPath = `${pathname}1`;
  } else {
    emtLinksPath = `${pathname}/1`;
  }

  const ZoneCard = ({ children }) => {
    return <div className="zone-card">{children}</div>;
  };

  ZoneCard.propTypes = {
    children: node.isRequired
  };

  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR;
  const store = useStore({ varnish });
  const { storeId, zipCode } = store;
  const RECS_REQUEST_KEY = 'aGAQFG4j6QtVTSWqujfFYWeIU6BR5Mee';

  const isMobile = channel === 'mobile';

  const pageNumber = page === undefined ? '1' : page;
  const seoPageNumber = parseInt(pageNumber, 10);

  const { data, loading } = useDataModel('product', { variables: { itemId } });
  const { product } = data || {};

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (data && !loading) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
          'paginated-product-reviews.page-load',
          { product, pageType: 'reviews' }
        );
      }
    }
  }, []);

  return (
    <div className="sui-bg-primary">
      <div className="sui-max-w-screen-2xl sui-m-auto">
        <ErrorBoundary name="redirector">
          <ReviewsRedirector
            pathWithQueryParams={pathname + search}
            itemId={itemId}
          />
        </ErrorBoundary>

        <ErrorBoundary name="metadata">
          <PprMetadata itemId={itemId} page={page} />
        </ErrorBoundary>

        <div className="sui-px-3 2xl:sui-px-0 sui-mt-2">
          <ErrorBoundary name="breadcrumbs">
            <Breadcrumbs itemId={itemId} />
          </ErrorBoundary>
        </div>

        <div className="sui-px-3 2xl:sui-px-0 sui-mt-6 sui-mb-4">
          <ProductReviewsHeader itemId={itemId} />
        </div>

        <div className="sui-px-3 2xl:sui-px-0">
          <ErrorBoundary name="product-info-bar">
            <ProductInfoBar itemId={itemId} />
          </ErrorBoundary>
        </div>

        <div className="sui-grid sui-grid-cols-1 lg:sui-grid-cols-12 sui-w-full">
          <div className="sui-col-span-6 sui-px-3 2xl:sui-px-0">
            {isMobile && (
              <ErrorBoundary name="product-details">
                <ProductDetails itemId={itemId}>
                  <ProductDetails.Title itemId={itemId} link />
                  <ProductDetails.BrandCollection />
                  <ProductDetailsReviewsRatings itemId={itemId} isMobile={isMobile} />
                </ProductDetails>
              </ErrorBoundary>
            )}

            {!isMobile && (
              <ErrorBoundary name="media-gallery">
                <MediaGalleryDesktop itemId={itemId} mainImageFetchPriority="high" />
              </ErrorBoundary>
            )}

            {!isMobile && (
              <ErrorBoundary name="sharebelt-desktop">
                <Sharebelt horizontal itemId={itemId}>
                  <ShareBeltShareButton email />
                  <ShareBeltPrint />
                </Sharebelt>
              </ErrorBoundary>
            )}

          </div>

          {!isMobile && (
            <div className="lg:sui-col-start-8 lg:sui-col-span-4 sui-px-3 lg:sui-px-0">
              <ErrorBoundary name="product-details">
                <ProductDetails itemId={itemId}>
                  <ProductDetails.Title itemId={itemId} link />
                  <ProductDetails.BrandCollection />
                  <ProductDetailsReviewsRatings itemId={itemId} isMobile={isMobile} />
                </ProductDetails>
              </ErrorBoundary>
              <ErrorBoundary name="salient-points">
                <SalientPoints itemId={itemId} max={3} />
              </ErrorBoundary>
              <div className="sui-mt-3">
                <ViewDetailsLink itemId={itemId} trackingParam="?emt_ppr_vfpd" />
              </div>
            </div>
          )}

          {isMobile && (
            <ErrorBoundary name="media-gallery">
              <MediaGalleryCarousel itemId={itemId} firstItemFetchPriority="high" />
            </ErrorBoundary>
          )}
        </div>

        {isMobile && (
          <div className="sui-px-3 2xl:sui-px-0">
            <div className="sui-mt-3">
              <ViewDetailsLink itemId={itemId} trackingParam="?emt_ppr_vfpd" />
            </div>
          </div>
        )}

        <div className="sui-col-span-12">
          <div>
            {!isMobile && (
              <ErrorBoundary name="ratings-and-reviews">
                <RatingsAndReviewsDesktopImporter
                  itemId={itemId}
                  seoPageNumber={seoPageNumber}
                  hideOverlay
                  qctx={qctx}
                  hydrator={{
                    className: 'col__12-12'
                  }}
                  dynamic={{
                    pageType: 'productReviews'
                  }}
                />
              </ErrorBoundary>
            )}
            {isMobile && (
              <ErrorBoundary name="ratings-and-reviews">
                <RatingsAndReviewsMobileImporter
                  itemId={itemId}
                  seoPageNumber={seoPageNumber}
                  hideOverlay
                  qctx={qctx}
                  hydrator={{
                    className: 'col__12-12'
                  }}
                  dynamic={{
                    pageType: 'productReviews'
                  }}
                />
              </ErrorBoundary>
            )}
          </div>
          <Hydrator id="hydrator-feature-based-recommendations" className="grid" scrollBuffer={750}>
            <Row>
              <ErrorBoundary name="feature-based-recommendations">
                <FeatureBasedRecommendationsImporter
                  hideOnMobile
                  itemId={itemId}
                  qctx={qctx}
                  layoutGroup={{
                    tag: Row,
                  }}
                  hydrator={{
                    tag: ZoneCard,
                    className: 'grid',
                    scrollBuffer: 750
                  }}
                  dynamic={{
                    pageType: 'productReviews',
                  }}
                />
              </ErrorBoundary>
            </Row>
            <Row>
              {/* <DynamicRecsWrapperImporter
                schemaName="pip_alternatives"
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideATC
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: 750,
                  id: 'pip_alternatives'
                }}
                dynamic={{
                  pageType: 'productReviews'
                }}
              /> */}
              <DynamicRecsWrapper
                schemaName="pip_alternatives"
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideATC
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: 750,
                  id: 'pip_alternatives'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
            <ErrorBoundary name="emt-links">
              <QueryProvider
                cacheKey="reviews-emt-links"
                defaultVariables={{ storeId, zipCode }}
              >
                <Row>
                  <EmtLinks
                    path={emtLinksPath}
                    errorBoundary
                    dynamic={{ pageType: 'product-reviews' }}
                    hydrator={{ className: 'grid', scrollBuffer: 750 }}
                  />
                </Row>
              </QueryProvider>
            </ErrorBoundary>
          </Hydrator>
        </div>
      </div>
    </div>
  );
}

ProductReviews.dataModel = extend(
  {
    product: params({ itemId: stringType().isRequired() }).shape({
      itemId: stringType(),
      dataSources: stringType(),
      identifiers: shapeType({
        brandName: stringType(),
        canonicalUrl: stringType(),
        itemId: stringType(),
        modelNumber: stringType(),
        omsThdSku: stringType(),
        productLabel: stringType(),
        storeSkuNumber: stringType(),
        specialOrderSku: stringType(),
        upcGtin13: stringType()
      }),
      media: shapeType({
        image: shapeType({
          url: stringType()
        }).client(),
        images: arrayType(shapeType({
          url: stringType()
        }))
      }),
      taxonomy: shapeType({
        breadCrumbs: arrayType(shapeType({
          label: stringType(),
          url: stringType()
        }))
      })
    })
  },
  Breadcrumbs,
  DynamicRecsWrapperImporter,
  DynamicRecsWrapper,
  FeatureBasedRecommendationsImporter,
  MediaGalleryCarousel,
  MediaGalleryDesktop,
  ProductDetails,
  ProductDetailsReviewsRatings,
  ProductReviewsHeader,
  RatingsAndReviewsDesktopImporter,
  RatingsAndReviewsMobileImporter,
  ReviewsRedirector,
  SalientPoints,
  ViewDetailsLink,
  PprMetadata
);